import React, { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "./firebase";
import { motion, AnimatePresence } from "framer-motion";
import { API_BASE } from "./config";
import logo from './deadringers.webp'; 

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const token = await result.user.getIdToken();
      const response = await fetch(`${API_BASE}/user-role`, {
        headers: { Authorization: token },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed to fetch role");
      onLogin({ token, role: data.role, email: result.user.email });
    } catch (err) {
      alert(err.message);
    }
  };

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      setResetMessage("Please enter your email.");
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/send-reset-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Reset failed");
      setResetMessage("✅ Password reset link sent. Check your inbox.");
    } catch (err) {
      setResetMessage("❌ " + err.message);
    }
  };

  const closeModal = () => {
    setShowForgotModal(false);
    setResetEmail("");
    setResetMessage("");
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen font-sans text-gray-900 overflow-hidden">
      {/* Left Panel */}
      <div
        className="w-full md:w-1/2 flex flex-col justify-center items-center p-6 md:p-10 relative overflow-hidden"
        style={{
          backgroundImage: 'url("/vintage-phone-pattern.png")',
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundColor: "#0F0F0F",
        }}
      >
        <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
          <img
            src={logo}
            alt="Dead Ringers Logo"
            className="h-10 w-10 rounded-md shadow-md border border-white/20 object-contain bg-white p-1"
          />
          <div className="text-lg md:text-xl font-semibold tracking-wider text-white drop-shadow-md bg-[#209465] px-2 py-1 rounded-md">
            Dead Ringers
          </div>
        </div>

        <motion.h1
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="text-2xl md:text-[48px] font-extrabold uppercase text-center z-10 mb-4 drop-shadow-lg bg-[#2baf6f] px-4 py-2 rounded-md mt-12 md:mt-0"
          style={{ fontFamily: '"Montserrat", sans-serif' }}
        >
          Flip the Script
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="text-base md:text-lg max-w-md text-center font-medium z-10 text-white/90 px-4 py-2 bg-black/60 rounded-md"
        >
          Turn tired reporting into powerful, actionable insight. Data storytelling redefined.
        </motion.p>

        {/* Mobile Bottom Filler */}
        <div className="block md:hidden absolute bottom-0 left-0 w-full h-32" style={{
          backgroundImage: 'url("/vintage-phone-pattern.png")',
          backgroundRepeat: "repeat",
          backgroundSize: "cover",
          backgroundPosition: "bottom",
        }}></div>
      </div>

      {/* Right Panel */}
      <div className="w-full md:w-1/2 flex justify-center items-center bg-[#f4f4f4] p-6 md:p-10">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-md p-6 md:p-10 bg-white shadow-2xl rounded-xl border border-gray-300"
        >
          <h2 className="text-xl md:text-3xl font-extrabold mb-6 text-gray-800 text-center">
            Welcome Back
          </h2>

          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-800 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#F4A261]"
            />
          </div>

          <div className="mb-2">
            <label className="block text-sm font-semibold text-gray-800 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#F4A261]"
            />
          </div>

          <div className="text-right text-sm mb-6">
            <button
              type="button"
              onClick={() => {
                setShowForgotModal(true);
                setResetEmail(email);
              }}
              className="text-[#F4A261] hover:underline"
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            className="w-full bg-[#eb9f21] hover:bg-[#f8b653] text-white py-3 rounded-lg font-semibold shadow-md transition"
          >
            Sign In
          </button>
        </form>
      </div>

      <AnimatePresence>
        {showForgotModal && (
          <motion.div
            className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="bg-white p-6 md:p-8 rounded-lg shadow-xl max-w-md w-full relative"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4">Reset Your Password</h3>
              <input
                type="email"
                placeholder="Enter your email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="w-full border px-4 py-2 rounded mb-4"
              />
              {resetMessage && (
                <div className="text-sm mb-2 text-gray-700">{resetMessage}</div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 rounded border text-gray-600 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordReset}
                  className="px-4 py-2 rounded bg-[#F4A261] text-white hover:bg-[#e0894a]"
                >
                  Send Reset Link
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Login;
